import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Maximize, Minimize, ShieldAlert, VideoOff } from "lucide-react";
import { api, BACKEND_URL, errMsg } from "@/lib/api";

const randPos = () => ({ top: `${6 + Math.random() * 78}%`, left: `${4 + Math.random() * 62}%` });

const Watermark = ({ viewer }) => {
  const [pos, setPos] = useState(randPos);
  const [pos2, setPos2] = useState(randPos);
  useEffect(() => {
    const a = setInterval(() => setPos(randPos()), 4000);
    const b = setInterval(() => setPos2(randPos()), 7000);
    return () => { clearInterval(a); clearInterval(b); };
  }, []);
  const label = `${viewer.email}${viewer.uid ? ` • ${viewer.uid}` : ""}`;
  return (
    <div className="absolute inset-0 pointer-events-none select-none z-20 overflow-hidden" aria-hidden data-testid="video-watermark">
      <span className="absolute transition-all duration-[1500ms] ease-in-out text-[11px] sm:text-sm font-mono text-white/35 bg-black/15 px-2 py-0.5 rounded whitespace-nowrap" style={pos} dir="ltr">{label}</span>
      <span className="absolute transition-all duration-[2500ms] ease-in-out text-[10px] sm:text-xs font-mono text-white/20 whitespace-nowrap rotate-[-12deg]" style={pos2} dir="ltr">{viewer.name || label}</span>
    </div>
  );
};

const useDeterrents = (videoRef, enabled) => {
  const [shield, setShield] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const onVis = () => {
      if (document.hidden) { videoRef.current?.pause(); setShield(true); } else setShield(false);
    };
    const onKey = (e) => {
      if (e.key === "PrintScreen" || ((e.metaKey || e.ctrlKey) && e.shiftKey && ["s", "S", "3", "4", "5"].includes(e.key))) {
        videoRef.current?.pause();
        setShield(true);
        setTimeout(() => setShield(false), 2500);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("keyup", onKey);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("keydown", onKey);
    };
  }, [enabled, videoRef]);
  return shield;
};

export const SecurePlayer = ({ lessonId, onEnded }) => {
  const [state, setState] = useState({ loading: true });
  const [fs, setFs] = useState(false);
  const boxRef = useRef(null);
  const videoRef = useRef(null);
  const shield = useDeterrents(videoRef, state.type === "upload");

  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    api.get(`/lessons/${lessonId}/playback`)
      .then((r) => alive && setState({ ...r.data, loading: false }))
      .catch((e) => alive && setState({ loading: false, error: errMsg(e) }));
    return () => { alive = false; };
  }, [lessonId]);

  useEffect(() => {
    const h = () => {
      const el = document.fullscreenElement || document.webkitFullscreenElement;
      if (el && el === videoRef.current) {
        Promise.resolve((document.exitFullscreen || document.webkitExitFullscreen)?.call(document))
          .then(() => boxRef.current?.requestFullscreen?.())
          .catch(() => {});
        return;
      }
      setFs(el === boxRef.current);
    };
    document.addEventListener("fullscreenchange", h);
    document.addEventListener("webkitfullscreenchange", h);
    return () => {
      document.removeEventListener("fullscreenchange", h);
      document.removeEventListener("webkitfullscreenchange", h);
    };
  }, []);

  const toggleFs = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen();
    else boxRef.current?.requestFullscreen?.();
  }, []);

  const block = (e) => e.preventDefault();
  const onDbl = (e) => { e.preventDefault(); e.stopPropagation(); toggleFs(); };

  return (
    <div ref={boxRef} onContextMenu={block} data-testid="secure-player"
      className="video-shield relative w-full aspect-video bg-black rounded-2xl overflow-hidden select-none">
      {state.loading && <div className="absolute inset-0 grid place-items-center"><Loader2 className="animate-spin text-white/60" size={32} /></div>}
      {state.error && (
        <div className="absolute inset-0 grid place-items-center text-center p-6" data-testid="player-error">
          <div><ShieldAlert className="mx-auto mb-3 text-[var(--kh-pink)]" size={36} /><p className="text-white/80">{state.error}</p></div>
        </div>
      )}
      {state.type === "none" && (
        <div className="absolute inset-0 grid place-items-center text-white/60" data-testid="player-no-video"><div className="text-center"><VideoOff className="mx-auto mb-2" />لم يتم إضافة فيديو لهذا الدرس بعد</div></div>
      )}
      {state.type === "upload" && (
        <video ref={videoRef} key={state.src} src={state.src.startsWith("http") ? state.src : `${BACKEND_URL}${state.src}`} controls playsInline onEnded={onEnded}
          controlsList="nodownload nofullscreen noremoteplayback" disablePictureInPicture disableRemotePlayback
          onContextMenu={block} onDragStart={block} onDoubleClick={onDbl} className="w-full h-full" data-testid="lesson-video" />
      )}
      {state.type === "embed" && (
        <iframe key={state.src} src={state.src} title="lesson" className="w-full h-full" data-testid="lesson-embed"
          allow="autoplay; encrypted-media; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" />
      )}
      {state.viewer && <Watermark viewer={state.viewer} />}
      {shield && <div className="absolute inset-0 z-30 bg-black grid place-items-center text-white/70 text-sm" data-testid="player-shield">تم إيقاف التشغيل مؤقتاً لحماية المحتوى</div>}
      {(state.type === "upload" || state.type === "embed") && (
        <button onClick={toggleFs} data-testid="player-fullscreen-btn" aria-label="ملء الشاشة"
          className="absolute top-3 left-3 z-30 p-2 rounded-lg bg-black/50 text-white/80 hover:text-white hover:bg-black/70">
          {fs ? <Minimize size={18} /> : <Maximize size={18} />}
        </button>
      )}
    </div>
  );
};
